import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  type Api,
  ApiError,
  type CrmSettings,
  type InboundSmsOutcome,
} from "../api";

/**
 * M3 closed loop — settings + the mock-mode demo lever. While providers
 * are mocks the card says so plainly and offers "simulate a reply" so the
 * owner can watch a WON reply drive the CRM + conversion queue end-to-end.
 */
export function ClosedLoopCard({ api }: { api: Api }) {
  const [settings, setSettings] = useState<CrmSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<InboundSmsOutcome | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await api.crmSettings();
      setSettings(result);
      setPhone(result.smsNotifyNumber ?? "");
      setCurrency(result.defaultCurrency);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not load closed-loop settings. Try again.",
      );
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const trimmed = phone.trim();
      const result = await api.setCrmSettings({
        smsNotifyNumber: trimmed === "" ? null : trimmed,
        defaultCurrency: currency.trim().toUpperCase(),
      });
      setSettings((current) =>
        current
          ? {
              ...current,
              smsNotifyNumber: result.smsNotifyNumber,
              defaultCurrency: result.defaultCurrency,
            }
          : current,
      );
      setSaved(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const simulate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = reply.trim();
    if (!body || sending) return;
    setSending(true);
    setOutcome(null);
    setError(null);
    try {
      setOutcome(await api.simulateInboundSms(body));
      setReply("");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not process the reply. Try again.",
      );
    } finally {
      setSending(false);
    }
  };

  if (!settings) {
    return (
      <section className="card closed-loop-card">
        <div className="card-head">
          <div>
            <h2>Closed loop</h2>
            <p className="muted">
              SMS lead alerts and ad-platform conversions.
            </p>
          </div>
        </div>
        {error ? (
          <p className="notice">{error}</p>
        ) : (
          <p className="muted">Loading…</p>
        )}
      </section>
    );
  }

  const unconfigured = [
    settings.providers.message,
    settings.providers.conversions,
  ].filter((name) => name.startsWith("unconfigured:"));
  const mockMode =
    unconfigured.length === 0 &&
    (settings.providers.message === "mock" ||
      settings.providers.conversions === "mock");

  return (
    <section className="card closed-loop-card">
      <div className="card-head">
        <div>
          <h2>Closed loop</h2>
          <p className="muted">
            New leads reach your phone by text; replying WON, LOST or QUAL
            updates the lead and reports the conversion to your ad platform.
          </p>
        </div>
        {unconfigured.length > 0 && (
          <span
            className="pill pill-danger"
            title={`A provider is named but not connected (${unconfigured.join(", ")}). Nothing is sent or uploaded — messages fail closed and conversions wait in the queue until it is configured.`}
          >
            Provider not configured
          </span>
        )}
        {mockMode && (
          <span
            className="pill pill-muted"
            title="Texts and ad-platform uploads are recorded but not actually delivered yet — provider connections are coming."
          >
            Demo mode
          </span>
        )}
      </div>

      <form className="closed-loop-settings" onSubmit={save}>
        <label>
          <span>Your mobile number</span>
          <input
            type="tel"
            placeholder="+447700900123"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setSaved(false);
            }}
            disabled={!settings.entitled.smsNotifications}
          />
        </label>
        <label>
          <span>Deal currency</span>
          <input
            type="text"
            maxLength={3}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setSaved(false);
            }}
          />
        </label>
        <button type="submit" disabled={saving}>
          {saving ? "Saving…" : saved ? "Saved" : "Save"}
        </button>
      </form>
      {!settings.entitled.smsNotifications && (
        <p className="muted small">
          Text alerts are available on the Growth plan.
        </p>
      )}

      {mockMode && settings.entitled.smsCrm && (
        <form className="closed-loop-simulate" onSubmit={simulate}>
          <label>
            <span>Try it: reply as if by text</span>
            <div className="simulate-row">
              <input
                type="text"
                placeholder='e.g. "WON 1500" or "QUAL"'
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <button type="submit" disabled={sending || !reply.trim()}>
                {sending ? "…" : "Send"}
              </button>
            </div>
          </label>
          {outcome && (
            <p className="muted small" data-testid="simulate-outcome">
              {outcome.kind === "no_lead"
                ? "No lead to apply this to yet — capture a lead first."
                : outcome.kind === "unrecognized"
                  ? "Kept as a note — replies update leads only when they start with WON, LOST, QUAL or CALL."
                  : `Lead ${outcome.leadId} → ${outcome.newStatus ?? outcome.kind}` +
                    (outcome.value !== null
                      ? ` (${currency} ${outcome.value})`
                      : "") +
                    (outcome.conversion === "queued"
                      ? " — conversion reported."
                      : outcome.conversion === "blocked_consent"
                        ? " — conversion NOT sent (no ad consent for this region)."
                        : "")}
            </p>
          )}
        </form>
      )}
      {error && <p className="notice">{error}</p>}
    </section>
  );
}
